import { inject, Injectable } from '@angular/core';
import { HostDataReaderService, QuoteToolStrategyService } from '@lx/quote-tool/shared/services';
import { SessionStorageService } from '@lx/shared/storage';
import { getCompany } from '@lx/shared/tokens';
import { COMPANIES } from '@lx/shared/types';
import { isEmptyObj } from '@lx/shared/utils';
import { Observable, of } from 'rxjs';
import { first, map, switchMap, tap } from 'rxjs/operators';
import { QuoteToolLiteState, SharedDataStore } from '../../../store';
import { RouteIds } from '../../../types';
import { StepDefinitionService } from '../../step-definition.service';
import { Flow, ALPADIA_FLOWS, KAPLAN_FLOWS, getQuoteToolStorageKey } from '@lx/quote-tool/lite/shared/types';

@Injectable({
  providedIn: 'root',
})
export class ActiveFlowModeStrategyService implements QuoteToolStrategyService<RouteIds> {
  private readonly sessionStorageService = inject(SessionStorageService);
  private readonly stepDefinitionService = inject(StepDefinitionService);
  private readonly hostDataReader = inject(HostDataReaderService);
  private readonly sharedDataStore = inject(SharedDataStore);
  private readonly company = getCompany();
  private readonly storageKey = getQuoteToolStorageKey();

  getStepToNavigate(): Observable<RouteIds> | RouteIds {
    return of(RouteIds.course);
  }

  loadData$(): Observable<boolean> {
    const flow: Flow = this.hostDataReader.getQuoteToolMode() as Flow;

    // If the flow is not valid, return false immediately
    if (!this.isExistingFlow(flow)) {
      return of(false);
    }

    // Load flow-specific languages first, then get the default language
    return this.sharedDataStore.loadLanguagesForFlow(flow).pipe(
      first(),
      switchMap(() => this.sharedDataStore.getDefaultLanguage()),
      first(),
      map((defaultLanguage: string) => {
        if (this.isExistingFlow(flow)) {
          return this.prepareStoreData(flow, defaultLanguage);
        }
        return undefined;
      }),
      tap((data: Partial<QuoteToolLiteState>) => {
        if (!isEmptyObj(data)) {
          this.sessionStorageService.set(this.storageKey, data);
        }
      }),
      map((selectedData: Partial<QuoteToolLiteState>) => !isEmptyObj(selectedData)),
    );
  }
  private isExistingFlow(quoteToolModeQueryParam: Flow): boolean {
    if (this.company === COMPANIES.KAPLAN) {
      return KAPLAN_FLOWS.includes(quoteToolModeQueryParam);
    } else if (this.company === COMPANIES.ALPADIA) {
      return ALPADIA_FLOWS.includes(quoteToolModeQueryParam);
    }
    return false;
  }

  private prepareStoreData(quoteToolMode: Flow, defaultLanguage: string): Partial<QuoteToolLiteState & { currentStepId: string }> {
    return {
      currentStepId: this.stepDefinitionService.getStepDefinition(RouteIds.course, quoteToolMode)?.id,
      selectedLanguage: defaultLanguage,
      activeFlow: quoteToolMode,
    };
  }
}
